import { c as _c } from "react/compiler-runtime";
import { Box, CircularProgress } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import type { RouterOutputs } from "#/shared/api";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
import { projectBrowserSlice, selectCurrentPage, selectHasMore } from "../../model/projectBrowserSlice";
import { useProjectBrowserContext } from "./ProjectBrowserContext";
import { ProjectBrowserEmpty } from "./ProjectBrowserEmpty";
import { ProjectBrowserItem } from "./ProjectBrowserItem";
import { ProjectBrowserItemSkeleton } from "./ProjectBrowserItemSkeleton";
type ProjectBrief = RouterOutputs["project"]["browseProjects"]["projects"][number];
type ProjectBrowserListProps = {
  projects: ProjectBrief[] | undefined;
  isLoading: boolean;
  selectedProjectSlug?: string;
  onSelectProject: (slug: string) => void;
};

// Fixed item height for virtualization - matches actual rendered height
const ITEM_HEIGHT = 76;

// Space after last item when bottom nav bar overlaps (mobile playground)
const BOTTOM_SPACER_HEIGHT = "calc(38px + env(safe-area-inset-bottom, 0px))";

// List component for Virtuoso
const VirtuosoList: React.FC<{
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}> = t0 => {
  const $ = _c(9);
  let children;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = ref;
  } else {
    children = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      listStyle: "none",
      p: 0,
      m: 0
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== props || $[7] !== ref) {
    t2 = <Box component="ul" role="list" ref={ref} {...props} sx={t1}>{children}</Box>;
    $[5] = children;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const ProjectBrowserList: React.FC<ProjectBrowserListProps> = t0 => {
  const $ = _c(46);
  const {
    projects,
    isLoading,
    selectedProjectSlug,
    onSelectProject
  } = t0;
  const dispatch = useAppDispatch();
  const isMobile = useMobileLayout();
  const {
    searchQuery,
    selectedCategories,
    selectedDifficulties,
    showOnlyNew
  } = useProjectBrowserContext();
  const currentPage = useAppSelector(selectCurrentPage);
  const hasMoreData = useAppSelector(selectHasMore);
  let t1;
  if ($[0] !== projects) {
    t1 = projects ?? [];
    $[0] = projects;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const displayedProjects = t1;
  let t2;
  if ($[2] !== currentPage || $[3] !== dispatch || $[4] !== hasMoreData || $[5] !== isLoading) {
    t2 = () => {
      if (hasMoreData && !isLoading) {
        dispatch(projectBrowserSlice.actions.setCurrentPage(currentPage + 1));
      }
    };
    $[2] = currentPage;
    $[3] = dispatch;
    $[4] = hasMoreData;
    $[5] = isLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleEndReached = t2;
  let t3;
  if ($[7] !== onSelectProject || $[8] !== selectedProjectSlug) {
    t3 = (_index, project) => {
      const handleClick = () => onSelectProject(project.slug);
      return <Box component="li" sx={{
        listStyle: "none"
      }}><ProjectBrowserItem project={project} isSelected={project.slug === selectedProjectSlug} onClick={handleClick} /></Box>;
    };
    $[7] = onSelectProject;
    $[8] = selectedProjectSlug;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const itemContent = t3;
  let t4;
  if ($[10] !== hasMoreData || $[11] !== isLoading) {
    t4 = isLoading && hasMoreData && <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2
    }}><CircularProgress size={24} /></Box>;
    $[10] = hasMoreData;
    $[11] = isLoading;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== isMobile) {
    t5 = isMobile && <Box sx={{
      height: BOTTOM_SPACER_HEIGHT
    }} aria-hidden={true} />;
    $[13] = isMobile;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const Footer = t6;
  let t7;
  if ($[18] !== Footer) {
    t7 = {
      List: VirtuosoList,
      Footer: () => Footer
    };
    $[18] = Footer;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const components = t7;
  const isInitialLoad = isLoading && displayedProjects.length === 0;
  const skeletonItemContent = _temp;
  if (isInitialLoad) {
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        height: "100%",
        width: "100%"
      };
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = Array.from({
        length: 10
      }, _temp2);
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        height: "100%",
        width: "100%"
      };
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== isMobile) {
      t11 = {
        List: VirtuosoList,
        Footer: () => isMobile ? <Box sx={{
          height: BOTTOM_SPACER_HEIGHT
        }} aria-hidden={true} /> : null
      };
      $[23] = isMobile;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] !== t11 || $[26] !== t9) {
      t12 = <Box sx={t8} aria-live="polite" aria-busy={true}><Virtuoso data={t9} totalCount={10} itemContent={skeletonItemContent} fixedItemHeight={ITEM_HEIGHT} style={t10} components={t11} /></Box>;
      $[25] = t11;
      $[26] = t9;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  if (!isLoading && displayedProjects.length === 0) {
    let t8;
    if ($[28] !== searchQuery || $[29] !== selectedCategories || $[30] !== selectedDifficulties || $[31] !== showOnlyNew) {
      t8 = searchQuery.trim().length > 0 || selectedCategories.length > 0 || selectedDifficulties.length > 0 || showOnlyNew;
      $[28] = searchQuery;
      $[29] = selectedCategories;
      $[30] = selectedDifficulties;
      $[31] = showOnlyNew;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    const hasFilters = t8;
    let t9;
    if ($[33] !== hasFilters || $[34] !== searchQuery) {
      t9 = <ProjectBrowserEmpty searchQuery={searchQuery} hasFilters={hasFilters} />;
      $[33] = hasFilters;
      $[34] = searchQuery;
      $[35] = t9;
    } else {
      t9 = $[35];
    }
    return t9;
  }
  let t8;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      height: "100%",
      width: "100%"
    };
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      height: "100%",
      width: "100%"
    };
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== components || $[39] !== displayedProjects || $[40] !== handleEndReached || $[41] !== itemContent) {
    t10 = <Virtuoso data={displayedProjects} totalCount={displayedProjects.length} itemContent={itemContent} endReached={handleEndReached} fixedItemHeight={ITEM_HEIGHT} style={t9} components={components} />;
    $[38] = components;
    $[39] = displayedProjects;
    $[40] = handleEndReached;
    $[41] = itemContent;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== isLoading || $[44] !== t10) {
    t11 = <Box sx={t8} aria-live="polite" aria-busy={isLoading}>{t10}</Box>;
    $[43] = isLoading;
    $[44] = t10;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
};
function _temp(_index_0) {
  return <Box component="li" sx={{
    listStyle: "none"
  }}><ProjectBrowserItemSkeleton /></Box>;
}
function _temp2(_, i) {
  return i;
}